import { ApiResponse, AuthResponse, RefreshTokenResponse } from '@/types';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

class ApiService {
  private api: AxiosInstance;
  private readonly baseURL: string;
  private readonly tokenKey: string;
  private readonly refreshTokenKey: string;
  private readonly tokenExpirationKey: string;
  private readonly refreshTokenExpirationKey: string;

  constructor() {
    this.baseURL =
      import.meta.env.VITE_API_BASE_URL || 'http://localhost:8084/api/v1';
    this.tokenKey = import.meta.env.VITE_JWT_TOKEN_KEY || 'loomis_auth_token';
    this.refreshTokenKey =
      import.meta.env.VITE_JWT_REFRESH_TOKEN_KEY || 'loomis_refresh_token';
    this.tokenExpirationKey = `${this.tokenKey}ea`;
    this.refreshTokenExpirationKey = `${this.refreshTokenKey}ea`;

    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor to add token
    this.api.interceptors.request.use(
      config => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => Promise.reject(error)
    );

    // Response interceptor to handle token refresh
    this.api.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            if (this.isRefreshTokenExpired()) {
              throw new Error('Refresh token expired');
            }

            const refreshToken = this.getRefreshToken();
            if (refreshToken) {
              const response = await this.refreshToken(refreshToken);
              this.setToken(
                response.data.data.access_token,
                response.data.data.expires_in
              );

              // Retry the original request with new token
              originalRequest.headers.Authorization = `Bearer ${response.data.data.access_token}`;
              return this.api(originalRequest);
            }
          } catch {
            // Refresh failed, logout user
            this.logout();
            // Redirect to dashboard since login functionality has been removed
            const dashboardUrl = new URL('/dashboard', window.location.origin);
            window.location.assign(dashboardUrl.toString());
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Token management
  public setToken(token: string, expiresInSeconds?: number): void {
    localStorage.setItem(this.tokenKey, token);
    if (expiresInSeconds) {
      const expirationDate = new Date(Date.now() + expiresInSeconds * 1000);
      localStorage.setItem(
        this.tokenExpirationKey,
        expirationDate.toISOString()
      );
    }
  }

  public getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  public getTokenExpiration(): Date | null {
    const expiration = localStorage.getItem(this.tokenExpirationKey);
    return expiration ? new Date(expiration) : null;
  }

  public setRefreshToken(token: string, expiresInSeconds?: number): void {
    localStorage.setItem(this.refreshTokenKey, token);
    if (expiresInSeconds) {
      const expirationDate = new Date(Date.now() + expiresInSeconds * 1000);
      localStorage.setItem(
        this.refreshTokenExpirationKey,
        expirationDate.toISOString()
      );
    }
  }

  public getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  public getRefreshTokenExpiration(): Date | null {
    const expiration = localStorage.getItem(this.refreshTokenExpirationKey);
    return expiration ? new Date(expiration) : null;
  }

  public removeTokens(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.tokenExpirationKey);
    localStorage.removeItem(this.refreshTokenExpirationKey);
  }

  // Auth methods

  public async register(userData: {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
  }): Promise<AxiosResponse<ApiResponse<AuthResponse>>> {
    return this.api.post<ApiResponse<AuthResponse>>('/auth/register', userData);
  }

  public async logout(): Promise<void> {
    try {
      await this.api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.removeTokens();
    }
  }

  public async refreshToken(
    refreshToken: string
  ): Promise<AxiosResponse<ApiResponse<RefreshTokenResponse>>> {
    return this.api.post<ApiResponse<RefreshTokenResponse>>('/auth/refresh', {
      refresh_token: refreshToken,
    });
  }

  public async getCurrentUser(): Promise<AxiosResponse> {
    return this.api.get('/auth/me');
  }

  // Generic API methods
  public async get<T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.get<T>(url, config);
  }

  public async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.post<T>(url, data, config);
  }

  public async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.put<T>(url, data, config);
  }

  public async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.patch<T>(url, data, config);
  }

  public async delete<T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.delete<T>(url, config);
  }

  // Check if user is authenticated
  public isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    // Check if token is expired
    return !this.isRefreshTokenExpired();
  }

  // Check if token is expired
  public isTokenExpired(): boolean {
    const expiration = this.getTokenExpiration();
    if (!expiration) return false; // If no expiration date, assume token is valid
    return new Date() >= expiration;
  }

  // Check if refresh token is expired
  public isRefreshTokenExpired(): boolean {
    const expiration = this.getRefreshTokenExpiration();
    if (!expiration) return false; // If no expiration date, assume token is valid
    return new Date() >= expiration;
  }

  // KYC Documents submission method
  public async submitKycDocuments(
    token: string,
    files: File[],
    filesGroups: Array<{ filename: string; groupId: number }>
  ): Promise<
    AxiosResponse<{
      success: boolean;
      result: {
        success: boolean;
        uploaded_files: Array<{
          original_name: string;
          document_group_id: number;
          file_size: number;
        }>;
        total_files: number;
      };
      message: string;
    }>
  > {
    const formData = new FormData();

    // Add token
    formData.append('token', token);

    // Add files
    files.forEach(file => {
      formData.append('files[]', file);
    });

    // Add files groups as JSON string
    formData.append('files_groups', JSON.stringify(filesGroups));

    return this.api.post('/kyc/documents/submit-form', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
